import React, { useState } from "react";
import { ArrowRight, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { BRAND } from "../data/content";

const initial = { name: "", email: "", location: "", story_themes: "", connection_to_place: "" };

export default function BecomeStoryteller() {
  const [form, setForm] = useState(initial);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const onChange = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.connection_to_place) {
      toast.error("Please share your name, email and connection to place.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/storyteller", form);
      setDone(true);
      setForm(initial);
      toast.success("Thank you — your story matters.");
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="page-become-storyteller">
      <section className="relative pt-40 pb-20 md:pt-52 md:pb-28 bg-brand-ink text-brand-cream overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center opacity-40"
          style={{ backgroundImage: `url(${BRAND.elderStoryteller})` }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-brand-ink/60 to-brand-ink/95" />
        <div className="container-px relative z-10">
          <div className="overline text-brand-ochre">Become a Storyteller</div>
          <h1 className="font-serif text-5xl md:text-6xl lg:text-7xl mt-5 leading-[1.04] max-w-4xl">
            Your voice can <span className="italic font-light">carry a place forward.</span>
          </h1>
        </div>
      </section>

      <section className="py-20 md:py-28">
        <div className="container-px grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-5">
            <div className="overline">Who we work with</div>
            <h2 className="font-serif text-4xl md:text-5xl mt-3 leading-tight text-brand-charcoal">
              You do not need to be a professional storyteller.
            </h2>
            <p className="mt-6 text-brand-muted text-lg font-light leading-relaxed">
              The most valuable stories often belong to the people who live within a
              landscape. Talking Trails works with Elders, historians, artists, guides,
              local residents and community storytellers to preserve and share knowledge
              for future generations.
            </p>
            <p className="mt-4 text-brand-muted text-lg font-light leading-relaxed">
              You simply need a connection to place.
            </p>

            <ul className="mt-10 space-y-3">
              {[
                "Elders & cultural knowledge holders",
                "Local historians and heritage groups",
                "Rangers, guides and naturalists",
                "Long-standing community members",
                "Artists, musicians and writers",
              ].map((l) => (
                <li key={l} className="flex items-start gap-3 text-brand-charcoal/80 font-light">
                  <span className="mt-2 w-1.5 h-1.5 bg-brand-ochre rounded-full shrink-0" />
                  {l}
                </li>
              ))}
            </ul>
          </div>

          <div className="lg:col-span-6 lg:col-start-7">
            {done ? (
              <SuccessCard onReset={() => setDone(false)} />
            ) : (
              <form
                onSubmit={submit}
                data-testid="storyteller-form"
                className="bg-brand-cream border border-brand-line/70 p-8 md:p-12"
              >
                <div className="overline">Share your story</div>
                <h3 className="font-serif text-3xl mt-3 text-brand-charcoal">Tell us about you and your place.</h3>

                <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-6">
                  <Field label="Your name *" testid="st-name">
                    <input
                      required
                      data-testid="st-input-name"
                      value={form.name}
                      onChange={onChange("name")}
                      className="form-input"
                      placeholder="e.g. Joan Williams"
                    />
                  </Field>
                  <Field label="Email *" testid="st-email">
                    <input
                      required
                      type="email"
                      data-testid="st-input-email"
                      value={form.email}
                      onChange={onChange("email")}
                      className="form-input"
                      placeholder="you@email.com"
                    />
                  </Field>
                </div>

                <Field label="Place / Country / town" testid="st-location" className="mt-6">
                  <input
                    data-testid="st-input-location"
                    value={form.location}
                    onChange={onChange("location")}
                    className="form-input"
                    placeholder="e.g. Kuranda, FNQ"
                  />
                </Field>

                <Field label="Story themes" testid="st-themes" className="mt-6">
                  <input
                    data-testid="st-input-themes"
                    value={form.story_themes}
                    onChange={onChange("story_themes")}
                    className="form-input"
                    placeholder="e.g. rivers, rail heritage, cultural knowledge"
                  />
                </Field>

                <Field label="Your connection to place *" testid="st-connection" className="mt-6">
                  <textarea
                    required
                    rows={5}
                    data-testid="st-input-connection"
                    value={form.connection_to_place}
                    onChange={onChange("connection_to_place")}
                    className="form-input resize-none"
                    placeholder="Share a little about your connection to the place and the stories you would like to keep alive."
                  />
                </Field>

                <button
                  type="submit"
                  data-testid="st-submit"
                  disabled={loading}
                  className="mt-8 inline-flex items-center gap-2 bg-brand-rainforest text-brand-cream px-8 py-4 text-xs uppercase tracking-[0.22em] hover:bg-brand-rainforestDark transition-colors disabled:opacity-60"
                >
                  {loading ? <Loader2 size={14} className="animate-spin" /> : <ArrowRight size={14} />}
                  {loading ? "Sending" : "Become a Storyteller"}
                </button>
              </form>
            )}
          </div>
        </div>
      </section>

      <style>{`
        .form-input {
          width: 100%;
          background: transparent;
          border: none;
          border-bottom: 1px solid rgba(38,38,38,0.25);
          padding: 12px 0;
          font-family: 'Work Sans', sans-serif;
          font-size: 0.95rem;
          color: #262626;
          outline: none;
          transition: border-color 240ms ease;
        }
        .form-input::placeholder { color: rgba(38,38,38,0.4); }
        .form-input:focus { border-color: #C06E52; }
      `}</style>
    </div>
  );
}

function Field({ label, children, testid, className = "" }) {
  return (
    <label className={`block ${className}`} data-testid={testid}>
      <span className="block overline text-brand-muted">{label}</span>
      <div className="mt-1">{children}</div>
    </label>
  );
}

function SuccessCard({ onReset }) {
  return (
    <div
      data-testid="storyteller-success"
      className="bg-brand-rainforest text-brand-cream p-10 md:p-14 relative overflow-hidden"
    >
      <div className="overline text-brand-cream/80">Received</div>
      <h3 className="font-serif text-4xl mt-3 leading-tight">Thank you — your story matters.</h3>
      <p className="mt-5 text-brand-cream/80 font-light leading-relaxed max-w-md">
        We will be in touch within a few days to talk further. Stories take time, and we
        want to listen well before recording anything.
      </p>
      <button
        onClick={onReset}
        className="mt-8 inline-flex items-center gap-2 border border-brand-cream/80 text-brand-cream px-6 py-3 text-xs uppercase tracking-[0.22em] hover:bg-brand-cream hover:text-brand-rainforest transition-colors"
      >
        Submit another
      </button>
    </div>
  );
}
